import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUrlFilters } from '../../hooks/useUrlFilters'
import {
  Instagram, Linkedin, Youtube, TrendingUp, Users, Eye, Heart,
  MessageCircle, Share2, Bookmark, RefreshCw, Link2, ExternalLink, BarChart3,
  ArrowUp, ArrowDown, Clock, Globe,
} from 'lucide-react'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { socialApi, type SocialConnection, type SocialPost } from '../../api/endpoints'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import { Select } from '../../components/ui/Input'

const PLATFORM_ICONS: Record<string, any> = {
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  google_ads: BarChart3,
  google_analytics: Globe,
}

const PLATFORM_COLORS: Record<string, string> = {
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  linkedin: 'bg-blue-600',
  youtube: 'bg-red-600',
  google_ads: 'bg-yellow-500',
  google_analytics: 'bg-green-600',
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return n.toLocaleString()
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—'
  return n.toFixed(2) + '%'
}

export default function SocialAnalyticsPage() {
  const queryClient = useQueryClient()
  const [filters, setFilter] = useUrlFilters({ period: '7', date_from: '', date_to: '' })
  const period = filters.period

  // Compute effective days for API calls (custom range calculates from dates)
  const effectiveDays = (() => {
    if (period === 'custom' && filters.date_from && filters.date_to) {
      const from = new Date(filters.date_from)
      const to = new Date(filters.date_to)
      return Math.max(1, Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)))
    }
    return parseInt(period) || 7
  })()
  const [selectedConnection, setSelectedConnection] = useState<SocialConnection | null>(null)
  const [connectModalOpen, setConnectModalOpen] = useState(false)

  const { data: dashboardData } = useQuery({
    queryKey: ['social-dashboard', effectiveDays],
    queryFn: () => socialApi.getDashboard(effectiveDays),
  })

  const { data: connectionsData } = useQuery({
    queryKey: ['social-connections'],
    queryFn: () => socialApi.listConnections(),
  })

  const connections = connectionsData?.data || []
  const dashboard = dashboardData?.data
  const totals = dashboard?.totals || {}

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Cross-platform performance overview</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            options={[
              { value: '7', label: 'Last 7 days' },
              { value: '14', label: 'Last 14 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: 'custom', label: 'Custom Range' },
            ]}
            value={period}
            onChange={(e) => setFilter('period', e.target.value)}
          />
          {period === 'custom' && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => setFilter('date_from', e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
              />
              <span className="text-gray-400 text-sm hidden sm:inline">to</span>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => setFilter('date_to', e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
              />
            </div>
          )}
          <Button variant="primary" onClick={() => setConnectModalOpen(true)}>
            <Link2 size={16} /> Connect Platform
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 sm:gap-4 mb-6">
        <KpiCard label="Followers" value={fmtNum(totals.followers)} icon={Users} />
        <KpiCard label="Views" value={fmtNum(totals.views)} icon={Eye} />
        <KpiCard label="Reach" value={fmtNum(totals.reach)} icon={TrendingUp} />
        <KpiCard label="Interactions" value={fmtNum(totals.engagements)} icon={Heart} />
        <KpiCard label="Eng. Rate" value={fmtPct(totals.engagement_rate)} icon={BarChart3} />
        <KpiCard label="Posts" value={fmtNum(totals.posts_published)} icon={Share2} />
        <KpiCard label="Follows" value={fmtNum(totals.follows)} icon={Users} />
        <KpiCard label="Unfollows" value={fmtNum(totals.unfollows)} icon={Users} />
      </div>

      {/* Connected Platforms */}
      {connections.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Globe size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No platforms connected</h3>
          <p className="text-sm text-gray-500 mb-4">Connect your social media accounts to start tracking performance.</p>
          <Button onClick={() => setConnectModalOpen(true)}>
            <Link2 size={16} /> Connect Platform
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {connections.map(conn => (
            <PlatformCard
              key={conn.id}
              connection={conn}
              period={effectiveDays}
              onSelect={() => setSelectedConnection(conn)}
            />
          ))}
        </div>
      )}

      {/* Selected platform drill-down */}
      {selectedConnection && (
        <PlatformDrillDown
          connection={selectedConnection}
          period={effectiveDays}
          onClose={() => setSelectedConnection(null)}
        />
      )}

      {/* Connect Platform Modal */}
      <ConnectPlatformModal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onConnected={() => {
          queryClient.invalidateQueries({ queryKey: ['social-connections'] })
          queryClient.invalidateQueries({ queryKey: ['social-dashboard'] })
        }}
      />
    </div>
  )
}


function KpiCard({ label, value, icon: Icon }: { label: string; value: string; icon: any }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase">{label}</span>
        <Icon size={16} className="text-gray-400" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}


function PlatformCard({ connection, period, onSelect }: {
  connection: SocialConnection
  period: number
  onSelect: () => void
}) {
  const queryClient = useQueryClient()
  const Icon = PLATFORM_ICONS[connection.platform] || Globe
  const colorClass = PLATFORM_COLORS[connection.platform] || 'bg-gray-600'

  const { data: summaryData } = useQuery({
    queryKey: ['social-summary', connection.id, period],
    queryFn: () => socialApi.getSummary(connection.id, period),
  })

  const syncMutation = useMutation({
    mutationFn: () => socialApi.syncConnection(connection.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-summary', connection.id] })
      queryClient.invalidateQueries({ queryKey: ['social-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['social-connections'] })
    },
  })

  const summary = summaryData?.data || {} as Record<string, unknown>

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Platform header */}
      <div className={`${colorClass} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <Icon size={20} />
          <div>
            <p className="font-semibold text-sm">{connection.account_name || connection.platform}</p>
            <p className="text-xs opacity-80 capitalize">{connection.platform}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
            connection.status === 'active' ? 'bg-white/20 text-white' : 'bg-red-400/30 text-white'
          }`}>
            {connection.status}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); syncMutation.mutate() }}
            className="text-white/80 hover:text-white"
            title="Sync now"
          >
            <RefreshCw size={14} className={syncMutation.isPending ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <MetricCell label="Followers" value={fmtNum(summary.current_followers as number)} delta={summary.followers_change as number} />
          <MetricCell label="Views" value={fmtNum(summary.total_views as number)} />
          <MetricCell label="Reach" value={fmtNum(summary.total_reach as number)} />
          <MetricCell label="Interactions" value={fmtNum(summary.total_engagements as number)} />
          <MetricCell label="Profile Views" value={fmtNum(summary.total_profile_views as number)} />
          <MetricCell label="Link Taps" value={fmtNum(summary.total_link_taps as number)} />
          <MetricCell label="Follows" value={fmtNum(summary.total_follows as number)} />
          <MetricCell label="Unfollows" value={fmtNum(summary.total_unfollows as number)} />
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            {connection.last_synced_at
              ? `Synced ${new Date(connection.last_synced_at).toLocaleDateString()}`
              : 'Never synced'
            }
          </span>
          <button
            onClick={onSelect}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
          >
            View Details <ExternalLink size={12} />
          </button>
        </div>
      </div>
    </div>
  )
}


function MetricCell({ label, value, delta }: { label: string; value: string; delta?: number | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
      {delta != null && delta !== 0 && (
        <span className={`text-[10px] flex items-center gap-0.5 ${delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
          {delta > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
          {delta > 0 ? '+' : ''}{fmtNum(delta)}
        </span>
      )}
    </div>
  )
}


function PlatformDrillDown({ connection, period, onClose }: {
  connection: SocialConnection
  period: number
  onClose: () => void
}) {
  type TabType = 'trends' | 'top' | 'bottom' | 'types' | 'times' | 'hashtags' | 'demographics' | 'all' | 'metrics'
  const [tab, setTab] = useState<TabType>('trends')

  const { data: metricsData } = useQuery({
    queryKey: ['social-metrics', connection.id, period],
    queryFn: () => socialApi.getMetrics(connection.id, period),
    enabled: tab === 'metrics' || tab === 'trends',
  })

  const { data: topData } = useQuery({
    queryKey: ['social-top-posts', connection.id],
    queryFn: () => socialApi.getTopPosts(connection.id, 'engagements', 10),
    enabled: tab === 'top',
  })

  const { data: bottomData } = useQuery({
    queryKey: ['social-bottom-posts', connection.id],
    queryFn: () => socialApi.getBottomPosts(connection.id, 'engagement_rate', 10),
    enabled: tab === 'bottom',
  })

  const { data: allPostsData } = useQuery({
    queryKey: ['social-all-posts', connection.id],
    queryFn: () => socialApi.getPosts(connection.id, { limit: '100' }),
    enabled: tab === 'all',
  })

  const { data: typeBreakdown } = useQuery({
    queryKey: ['social-type-breakdown', connection.id],
    queryFn: () => socialApi.getPostTypeBreakdown(connection.id),
    enabled: tab === 'types',
  })

  const { data: bestTimes } = useQuery({
    queryKey: ['social-best-times', connection.id],
    queryFn: () => socialApi.getBestPostingTimes(connection.id),
    enabled: tab === 'times',
  })

  const { data: hashtagData } = useQuery({
    queryKey: ['social-hashtags', connection.id],
    queryFn: () => socialApi.getHashtagPerformance(connection.id),
    enabled: tab === 'hashtags',
  })

  const { data: demoData } = useQuery({
    queryKey: ['social-demographics', connection.id],
    queryFn: () => socialApi.getDemographics(connection.id),
    enabled: tab === 'demographics',
  })

  const Icon = PLATFORM_ICONS[connection.platform] || Globe

  const tabLabels: Record<TabType, string> = {
    trends: 'Trends', top: 'Top Posts', bottom: 'Bottom Posts',
    types: 'Post Types', times: 'Best Times', hashtags: 'Hashtags',
    demographics: 'Audience', all: 'All Posts', metrics: 'Raw Data',
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-6">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">{connection.account_name}</h3>
          <span className="text-xs text-gray-400 capitalize">{connection.platform}</span>
        </div>
        <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600">Close</button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {(Object.keys(tabLabels) as TabType[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tabLabels[t]}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === 'trends' && (
          <TrendsCharts data={(metricsData?.data || []).slice().reverse()} />
        )}
        {tab === 'metrics' && (
          <MetricsTable data={metricsData?.data || []} />
        )}
        {tab === 'top' && (
          <PostsGrid posts={topData?.data || []} label="Top performing" />
        )}
        {tab === 'bottom' && (
          <PostsGrid posts={bottomData?.data || []} label="Lowest performing" />
        )}
        {tab === 'types' && (
          <PostTypeBreakdown data={typeBreakdown?.data || []} />
        )}
        {tab === 'times' && (
          <BestPostingTimes data={bestTimes?.data} />
        )}
        {tab === 'hashtags' && (
          <HashtagPerformance data={hashtagData?.data || []} />
        )}
        {tab === 'demographics' && (
          <AudienceDemographics data={demoData?.data} />
        )}
        {tab === 'all' && (
          <PostsGrid posts={allPostsData?.data || []} label="All" />
        )}
      </div>
    </div>
  )
}


function MetricsTable({ data }: { data: Array<Record<string, any>> }) {
  if (!data.length) return <p className="text-sm text-gray-400 py-4 text-center">No metrics data yet. Sync the connection first.</p>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 uppercase border-b">
            <th className="text-left py-2 px-3">Date</th>
            <th className="text-right py-2 px-3">Followers</th>
            <th className="text-right py-2 px-3">Change</th>
            <th className="text-right py-2 px-3">Views</th>
            <th className="text-right py-2 px-3">Reach</th>
            <th className="text-right py-2 px-3">Interactions</th>
            <th className="text-right py-2 px-3">Profile Views</th>
            <th className="text-right py-2 px-3">Link Taps</th>
            <th className="text-right py-2 px-3">Follows</th>
            <th className="text-right py-2 px-3">Unfollows</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row, i) => {
            const extra = row.platform_metrics || {}
            return (
              <tr key={i} className="hover:bg-gray-50">
                <td className="py-2 px-3 text-gray-600">{row.date ? new Date(row.date).toLocaleDateString() : '—'}</td>
                <td className="py-2 px-3 text-right font-medium">{fmtNum(row.followers)}</td>
                <td className="py-2 px-3 text-right">
                  {row.followers_delta != null && (
                    <span className={row.followers_delta > 0 ? 'text-green-600' : row.followers_delta < 0 ? 'text-red-500' : 'text-gray-400'}>
                      {row.followers_delta > 0 ? '+' : ''}{row.followers_delta}
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-right">{fmtNum(extra.views)}</td>
                <td className="py-2 px-3 text-right">{fmtNum(row.reach)}</td>
                <td className="py-2 px-3 text-right">{fmtNum(row.engagements)}</td>
                <td className="py-2 px-3 text-right">{fmtNum(row.profile_views)}</td>
                <td className="py-2 px-3 text-right">{fmtNum(extra.profile_links_taps)}</td>
                <td className="py-2 px-3 text-right">
                  {extra.follows != null && <span className="text-green-600">+{extra.follows}</span>}
                </td>
                <td className="py-2 px-3 text-right">
                  {extra.unfollows != null && <span className="text-red-500">-{extra.unfollows}</span>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


function PostsGrid({ posts, label }: { posts: SocialPost[]; label: string }) {
  if (!posts.length) return <p className="text-sm text-gray-400 py-4 text-center">No posts synced yet. Sync the connection first.</p>

  const typeColors: Record<string, string> = {
    reel: 'bg-purple-100 text-purple-700',
    video: 'bg-blue-100 text-blue-700',
    carousel: 'bg-amber-100 text-amber-700',
    post: 'bg-gray-100 text-gray-600',
  }

  // Count by type
  const typeCounts: Record<string, number> = {}
  posts.forEach(p => { typeCounts[p.post_type || 'post'] = (typeCounts[p.post_type || 'post'] || 0) + 1 })

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs text-gray-400">{label} — {posts.length} posts</p>
        {Object.entries(typeCounts).map(([type, count]) => (
          <span key={type} className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${typeColors[type] || 'bg-gray-100 text-gray-500'}`}>
            {type}: {count}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.map(post => (
          <div key={post.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
            {/* Thumbnail or type indicator */}
            {post.thumbnail_url || post.media_url ? (
              <div className="w-full h-32 bg-gray-100 rounded mb-2 overflow-hidden relative">
                <img
                  src={post.thumbnail_url || post.media_url || ''}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
                {post.post_type && (
                  <span className={`absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${typeColors[post.post_type] || 'bg-gray-100 text-gray-500'}`}>
                    {post.post_type}
                  </span>
                )}
                {post.video_views != null && post.video_views > 0 && (
                  <span className="absolute bottom-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white">
                    {fmtNum(post.video_views)} views
                  </span>
                )}
              </div>
            ) : (
              <div className="w-full h-16 bg-gray-50 rounded mb-2 flex items-center justify-center">
                <span className={`text-xs px-2 py-1 rounded font-medium capitalize ${typeColors[post.post_type || 'post'] || 'bg-gray-100 text-gray-500'}`}>
                  {post.post_type || 'post'}
                </span>
              </div>
            )}

            {/* Caption */}
            <p className="text-xs text-gray-700 line-clamp-2 mb-2">{post.text || '(no caption)'}</p>

            {/* Metrics row */}
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-0.5" title="Likes"><Heart size={11} /> {fmtNum(post.likes)}</span>
              <span className="flex items-center gap-0.5" title="Comments"><MessageCircle size={11} /> {fmtNum(post.comments)}</span>
              <span className="flex items-center gap-0.5" title="Shares"><Share2 size={11} /> {fmtNum(post.shares)}</span>
              <span className="flex items-center gap-0.5" title="Saves"><Bookmark size={11} /> {fmtNum(post.saves)}</span>
            </div>

            {/* Secondary row */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
              <div className="flex items-center gap-2">
                {post.reach != null && <span>Reach: {fmtNum(post.reach)}</span>}
                {post.engagement_rate != null && <span>ER: {fmtPct(post.engagement_rate)}</span>}
              </div>
              <div className="flex items-center gap-1.5">
                {post.post_type && (
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 capitalize">{post.post_type}</span>
                )}
                {post.permalink && (
                  <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-700">
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>

            {/* Date */}
            {post.published_at && (
              <p className="text-[10px] text-gray-300 mt-1 flex items-center gap-1">
                <Clock size={9} />
                {new Date(post.published_at).toLocaleDateString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}


function TrendsCharts({ data }: { data: Array<Record<string, any>> }) {
  if (!data.length) return <p className="text-sm text-gray-400 py-4 text-center">No metrics data yet. Data builds up over daily syncs.</p>

  // Flatten platform_metrics into the chart data
  const chartData = data.map(d => {
    const extra = d.platform_metrics || {}
    return {
      ...d,
      ...extra,
      date: d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    }
  })

  const hasInsights = chartData.some((d: any) => d.reach > 0)
  const hasViews = chartData.some((d: any) => d.views > 0)
  const hasFollows = chartData.some((d: any) => d.follows > 0 || d.unfollows > 0)

  return (
    <div className="space-y-6">
      {/* Views & Reach */}
      {(hasInsights || hasViews) && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Views & Reach</h4>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={38} tickFormatter={(v: number) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
              <Tooltip />
              <Legend />
              {hasViews && <Area type="monotone" dataKey="views" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name="Views" />}
              <Area type="monotone" dataKey="reach" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} name="Reach" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Interactions & Profile Views */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Interactions & Profile Views</h4>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={38} tickFormatter={(v: number) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="engagements" stroke="#ec4899" fill="#ec4899" fillOpacity={0.15} name="Interactions" />
            <Area type="monotone" dataKey="profile_views" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} name="Profile Views" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Follows vs Unfollows */}
      {hasFollows && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Follows & Unfollows</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={35} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
              <Tooltip />
              <Legend />
              <Bar dataKey="follows" fill="#10b981" name="Follows" radius={[3, 3, 0, 0]} />
              <Bar dataKey="unfollows" fill="#ef4444" name="Unfollows" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Follower Count */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Followers</h4>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={38} tickFormatter={(v: number) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
            <Tooltip />
            <Area type="monotone" dataKey="followers" stroke="#10b981" fill="#10b981" fillOpacity={0.15} name="Followers" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}


function PostTypeBreakdown({ data }: { data: Array<Record<string, any>> }) {
  if (!data.length) return <p className="text-sm text-gray-400 py-4 text-center">No post data yet.</p>

  return (
    <div className="space-y-4">
      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="post_type" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 10 }} width={35} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
          <Tooltip />
          <Legend />
          <Bar dataKey="avg_likes" fill="#ec4899" name="Avg Likes" radius={[3, 3, 0, 0]} />
          <Bar dataKey="avg_comments" fill="#8b5cf6" name="Avg Comments" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* Table */}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 uppercase border-b">
            <th className="text-left py-2 px-3">Type</th>
            <th className="text-right py-2 px-3">Posts</th>
            <th className="text-right py-2 px-3">Avg Likes</th>
            <th className="text-right py-2 px-3">Avg Comments</th>
            <th className="text-right py-2 px-3">Avg ER</th>
            <th className="text-right py-2 px-3">Total Eng.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="py-2 px-3 capitalize font-medium">{row.post_type}</td>
              <td className="py-2 px-3 text-right">{row.count}</td>
              <td className="py-2 px-3 text-right">{row.avg_likes}</td>
              <td className="py-2 px-3 text-right">{row.avg_comments}</td>
              <td className="py-2 px-3 text-right">{fmtPct(row.avg_engagement_rate)}</td>
              <td className="py-2 px-3 text-right font-medium">{fmtNum(row.total_engagements)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}


function BestPostingTimes({ data }: { data: any }) {
  if (!data) return <p className="text-sm text-gray-400 py-4 text-center">No posting time data yet.</p>

  return (
    <div className="space-y-6">
      {data.best_day && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="px-3 sm:px-4 py-3 bg-indigo-50 rounded-lg">
            <p className="text-[11px] text-indigo-600 uppercase font-medium">Best Day</p>
            <p className="text-base sm:text-lg font-bold text-indigo-900">{data.best_day}</p>
          </div>
          <div className="px-3 sm:px-4 py-3 bg-pink-50 rounded-lg">
            <p className="text-[11px] text-pink-600 uppercase font-medium">Best Time</p>
            <p className="text-base sm:text-lg font-bold text-pink-900">{data.best_hour}</p>
          </div>
          <div className="px-3 sm:px-4 py-3 bg-gray-50 rounded-lg">
            <p className="text-[11px] text-gray-500 uppercase font-medium">Posts</p>
            <p className="text-base sm:text-lg font-bold text-gray-900">{data.total_posts_analyzed}</p>
          </div>
        </div>
      )}

      {/* Day of week chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Avg Engagement by Day</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.by_day || []} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} width={35} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
            <Tooltip />
            <Bar dataKey="avg_engagement" fill="#6366f1" name="Avg Engagement" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Hour of day chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Avg Engagement by Hour</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.by_hour || []} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={35} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
            <Tooltip />
            <Bar dataKey="avg_engagement" fill="#ec4899" name="Avg Engagement" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}


function HashtagPerformance({ data }: { data: Array<Record<string, any>> }) {
  if (!data.length) return <p className="text-sm text-gray-400 py-4 text-center">No hashtag data yet.</p>

  return (
    <div>
      <ResponsiveContainer width="100%" height={Math.min(data.length * 35 + 40, 400)}>
        <BarChart data={data.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
          <YAxis dataKey="hashtag" type="category" tick={{ fontSize: 10 }} width={90} />
          <Tooltip />
          <Bar dataKey="avg_engagements" fill="#8b5cf6" name="Avg Engagements" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div className="overflow-x-auto">
      <table className="w-full text-sm mt-4">
        <thead>
          <tr className="text-xs text-gray-500 uppercase border-b">
            <th className="text-left py-2 px-3">Hashtag</th>
            <th className="text-right py-2 px-3">Posts</th>
            <th className="text-right py-2 px-3">Avg Eng.</th>
            <th className="text-right py-2 px-3">Total Eng.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="py-2 px-3 font-medium text-indigo-600">{row.hashtag}</td>
              <td className="py-2 px-3 text-right">{row.posts_count}</td>
              <td className="py-2 px-3 text-right">{fmtNum(row.avg_engagements)}</td>
              <td className="py-2 px-3 text-right">{fmtNum(row.total_engagements)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}


function AudienceDemographics({ data }: { data: any }) {
  if (!data || !data.available) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">
        Demographics data not available yet. Requires instagram_manage_insights permission and a sync after granting it.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {/* Gender + Age side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gender */}
        {data.gender && data.gender.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Gender</h4>
            <div className="space-y-2">
              {data.gender.map((g: any) => (
                <div key={g.label} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-16">{g.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        g.label === 'Female' ? 'bg-pink-400' :
                        g.label === 'Male' ? 'bg-blue-400' : 'bg-gray-400'
                      }`}
                      style={{ width: `${g.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-14 text-right">{g.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Age Groups */}
        {data.age_groups && data.age_groups.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Age Groups</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.age_groups}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: any) => `${value}%`} />
                <Bar dataKey="percentage" fill="#8b5cf6" name="%" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Cities + Countries side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Cities */}
        {data.top_cities && data.top_cities.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Top Cities</h4>
            <div className="space-y-1.5">
              {data.top_cities.map((c: any, i: number) => (
                <div key={c.label} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                  <span className="text-sm text-gray-700 flex-1 truncate">{c.label}</span>
                  <div className="w-24 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${c.percentage}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right">{c.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Countries */}
        {data.top_countries && data.top_countries.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Top Countries</h4>
            <div className="space-y-1.5">
              {data.top_countries.map((c: any, i: number) => (
                <div key={c.label} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                  <span className="text-sm text-gray-700 flex-1 truncate">{c.label}</span>
                  <div className="w-24 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-green-400 rounded-full" style={{ width: `${c.percentage}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right">{c.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center">
        Snapshot from {data.snapshot_date ? new Date(data.snapshot_date).toLocaleDateString() : 'latest sync'}
      </p>
    </div>
  )
}


function ConnectPlatformModal({ open, onClose, onConnected }: {
  open: boolean
  onClose: () => void
  onConnected: () => void
}) {
  const [step, setStep] = useState<'choose' | 'instagram-discover' | 'done'>('choose')
  const [result, setResult] = useState<string | null>(null)

  const discoverMutation = useMutation({
    mutationFn: () => socialApi.discoverInstagram(),
  })

  const connectIgMutation = useMutation({
    mutationFn: () => socialApi.connectInstagram(),
    onSuccess: (resp) => {
      setResult(resp.message || 'Connected!')
      setStep('done')
      onConnected()
    },
  })

  const handleClose = () => {
    setStep('choose')
    setResult(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title="Connect Platform">
      {step === 'choose' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 mb-4">Select a platform to connect:</p>

          <button
            onClick={() => {
              setStep('instagram-discover')
              discoverMutation.mutate()
            }}
            className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Instagram size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-medium text-sm text-gray-900">Instagram</p>
              <p className="text-xs text-gray-500">Connect via existing Meta connection</p>
            </div>
          </button>

          <button disabled className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg opacity-50 cursor-not-allowed">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Linkedin size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-medium text-sm text-gray-900">LinkedIn</p>
              <p className="text-xs text-gray-500">Coming soon (Phase 4)</p>
            </div>
          </button>

          <button disabled className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg opacity-50 cursor-not-allowed">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Youtube size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-medium text-sm text-gray-900">YouTube</p>
              <p className="text-xs text-gray-500">Coming soon (Phase 6)</p>
            </div>
          </button>
        </div>
      )}

      {step === 'instagram-discover' && (
        <div className="space-y-4">
          {discoverMutation.isPending && (
            <div className="text-center py-8">
              <RefreshCw size={24} className="mx-auto text-indigo-500 animate-spin mb-2" />
              <p className="text-sm text-gray-500">Discovering Instagram accounts...</p>
            </div>
          )}

          {discoverMutation.isError && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              Failed to discover Instagram accounts. Make sure you have an active Meta connection with Instagram permissions.
            </div>
          )}

          {discoverMutation.isSuccess && (
            <div>
              {(discoverMutation.data?.data || []).length === 0 ? (
                <div className="p-3 bg-amber-50 text-amber-700 text-sm rounded-lg">
                  No Instagram Business accounts found. Make sure your Facebook Page has an Instagram Business Account linked.
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Found Instagram accounts:</p>
                  {(discoverMutation.data?.data || []).map((ig: any) => (
                    <div key={ig.ig_user_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium text-sm">@{ig.username || ig.name}</p>
                        <p className="text-xs text-gray-500">{fmtNum(ig.followers_count)} followers</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => connectIgMutation.mutate()}
                        loading={connectIgMutation.isPending}
                      >
                        Connect
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-start pt-2">
            <Button variant="secondary" onClick={() => setStep('choose')}>Back</Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="text-center py-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Instagram size={24} className="text-green-600" />
          </div>
          <p className="font-medium text-gray-900 mb-1">{result}</p>
          <p className="text-sm text-gray-500 mb-4">You can now sync metrics and view analytics.</p>
          <Button onClick={handleClose}>Done</Button>
        </div>
      )}
    </Modal>
  )
}
